import { z } from 'zod';

/**
 * An alert to VagusX operations (ADR-318, P12) — a log line, not a service.
 *
 * Whatever raises one (the API before a dial, the voice worker mid-call) writes
 * ONE structured line, `console.error(opsAlertLogLine(alert))`. Cloud Run turns a JSON line into
 * a `jsonPayload` entry with its `severity`, and a log-based alert policy in
 * Cloud Monitoring matching `jsonPayload.event="ops_alert"` emails ops (that
 * policy is set up outside this repo's code). Throttling, recipients and
 * incident history live in the policy, so no code holds state or credentials
 * for it, and every service that can write a log can raise one.
 *
 * IDS ONLY, NEVER PATIENT DATA. The line is indexed in Cloud Logging and quoted
 * in the email, so the shape carries nothing a patient could be identified by:
 * `subjectId` is a uuid and `detail` is a short machine code with ids. Its
 * character set has no spaces, so a sentence — or a name typed into one — does
 * not fit.
 */
export const OPS_ALERT_KIND = {
  /** A campaign step's pinned workflow version could not be loaded. */
  WORKFLOW_PINNED_UNAVAILABLE: 'workflow_pinned_unavailable',
  /** It loaded but failed validation. */
  WORKFLOW_PINNED_INVALID: 'workflow_pinned_invalid',
  /**
   * A campaign call could not be prepared because of how the practice or the
   * campaign is set up (no outbound number, an unreadable campaign version).
   * Nothing was dialled; the run waits and will hit it again until fixed.
   */
  CAMPAIGN_DIAL_UNPREPARED: 'campaign_dial_unprepared',
  /**
   * A call reported an opt-out and it could not be written (ADR-318, E). The
   * completion fails closed and the worker retries; this says so at once,
   * because a patient who asked us to stop must never be called again for a
   * failed write. The subject is the interaction.
   */
  OPTOUT_WRITE_FAILED: 'optout_write_failed',
  /**
   * A campaign cannot run as configured: its spec will not parse, or its
   * membership could not be reconciled (ADR-318, E). The subject is the
   * campaign. The `campaign_error` event, until practices have a staff
   * notification path of their own.
   */
  CAMPAIGN_ERROR: 'campaign_error',
  /**
   * A call reported a callback time outside 08:00–21:00 (ADR-336). The voice
   * agent refuses such a time while the patient is on the line, so one that
   * arrives anyway is a worker with a bug. The driver moves it to the next
   * 08:00 and never rings at night. The subject is the interaction.
   */
  CALLBACK_OUTSIDE_CALLING_HOURS: 'callback_outside_calling_hours',
  /**
   * A run's calls keep going out and never reporting back (ADR-336): two in a
   * row. The first is given back and tried again; the second ends the run,
   * because a fault that hits every call must not redial a patient for ever.
   * The subject is the campaign.
   */
  CAMPAIGN_CALL_UNREPORTED: 'campaign_call_unreported',
  /**
   * A follow-up from an inbound call is still unassigned after routing: the
   * org has no rule that takes it, no inbound phone team with members, and no
   * catch-all, so it is in nobody's "My" tab (docs/call-follow-ups.md).
   * `subjectId` is the task; `detail` names the kind.
   */
  CALL_FOLLOW_UP_UNASSIGNED: 'call_follow_up_unassigned',
  /**
   * A completed call's `call/follow-ups` event could not be sent (ADR-353).
   * The call stays `pending` and the reconciler re-sends it within minutes,
   * so nothing is lost; repeated alerts mean the event bus is down and the
   * office's follow-ups are late. `subjectId` is the interaction.
   */
  CALL_FOLLOW_UPS_SEND_FAILED: 'call_follow_ups_send_failed',
  /**
   * Several of a practice's calls were lost in one reaper sweep (ADR-355):
   * the voice worker died with calls on it, usually a deploy or a scale-in.
   * One alert per practice per sweep, `detail` naming the count, so a deploy
   * that cut a dozen calls reads as one event. `subjectId` is the practice.
   */
  CALL_LOST_BURST: 'call_lost_burst',
} as const;
export type OpsAlertKind = (typeof OPS_ALERT_KIND)[keyof typeof OPS_ALERT_KIND];
export const OPS_ALERT_KIND_VALUES = Object.values(OPS_ALERT_KIND) as [
  OpsAlertKind,
  ...OpsAlertKind[],
];

/** The `event` field the Cloud Monitoring alert policy matches on. Renaming it silences every alert. */
export const OPS_ALERT_LOG_EVENT = 'ops_alert';

export const opsAlertSchema = z.object({
  kind: z.enum(OPS_ALERT_KIND_VALUES),
  organizationId: z.string().uuid(),
  /** What the alert is about: the version id for the workflow kinds, the campaign id otherwise. */
  subjectId: z.string().uuid(),
  /** e.g. `fetch_failed,status=404,campaign=<uuid>`. Letters, digits and `_-.:=,/` only. */
  detail: z
    .string()
    .max(300)
    .regex(/^[A-Za-z0-9_\-.:=,/]*$/, 'detail is a short code with ids, never free text'),
});
export type OpsAlert = z.infer<typeof opsAlertSchema>;

/**
 * The line to write for an alert, on stderr (`console.error`): single-line JSON
 * with `severity`, which Cloud Run's log agent parses into a structured entry.
 * Built here and written by the caller, because this package runs in no
 * particular runtime and has no `console` of its own.
 *
 * THE ALERT IS VALIDATED FIRST, and an invalid one is still logged — as an
 * `ops_alert` whose `kind` is `invalid_alert` and with nothing but the field
 * paths that failed. A caller with a bug in its alert must not be silenced by
 * that bug, and must not leak whatever free text it tried to put in `detail`.
 */
export function opsAlertLogLine(alert: OpsAlert): string {
  const parsed = opsAlertSchema.safeParse(alert);
  const fields = parsed.success
    ? parsed.data
    : {
        kind: 'invalid_alert',
        issues: parsed.error.issues.map((i) => i.path.join('.')),
      };
  return JSON.stringify({
    severity: 'ERROR',
    message: `[ops-alert] ${fields.kind}`,
    event: OPS_ALERT_LOG_EVENT,
    ...fields,
  });
}
